import type { PacingBlockV2 } from 'shared/src/api/line-item-types';
import type { LineItemApiResponse } from 'shared/src/api/line-item-api-types';
import type { UpdateLineItemPatch } from 'shared/src/api/ai-line-item-chat-types';
import type { PacingType, LineItemType } from 'shared/src/domain/line-item/line-item-types';
import {
  generateMonthlyBlocks,
  rescalePacingToTotal
} from 'shared/src/domain/line-item/pacing-helpers';

export type ResolvedPacing = {
  pacing_type: PacingType;
  pacing_details: PacingBlockV2[];
  monthly_rollover: boolean;
};

export type PacingPatchCurrent = Pick<
  LineItemApiResponse,
  | 'line_item_type'
  | 'dates'
  | 'price'
  | 'pacing_type'
  | 'pacing_details'
  | 'monthly_rollover'
  | 'media_budget'
  | 'estimated_units'
  | 'planned_unit_cost'
>;

// The type-aware paced total after merging the patch onto the current row (mirrors
// calcTotalPaced in pacing-validator-adapter.ts, but over a partial patch).
export function calcPatchedTotalPaced(
  current: PacingPatchCurrent,
  patch: Partial<UpdateLineItemPatch>
): number {
  switch (current.line_item_type) {
    case 'standard':
      return patch.price ?? current.price;
    case 'management_fee':
      return patch.media_budget ?? current.media_budget;
    case 'zero_dollar':
      return (
        (patch.estimated_units ?? current.estimated_units) *
        (patch.planned_unit_cost ?? current.planned_unit_cost)
      );
    case 'zero_margin':
      // Structurally excluded from ProposalLineItemApiUpdateRequest — the AI patch flow does
      // not support editing zero_margin line items (see media-team-line-item-form.tsx, which
      // treats it the same way).
      throw new Error('zero_margin line items are not supported by the AI update flow');
  }
}

// The paced total after the patch as the proposal side sees it: on a promoted item
// planned_unit_cost routes to execution and must not move the zero-dollar paced total.
export function calcEffectiveTotalPaced(
  current: PacingPatchCurrent,
  patch: UpdateLineItemPatch,
  promoted: boolean
): number {
  return calcPatchedTotalPaced(
    current,
    promoted ? { ...patch, planned_unit_cost: undefined } : patch
  );
}

export function mergePatchedDates(
  current: PacingPatchCurrent,
  patch: UpdateLineItemPatch
): PacingBlockV2['dates'] {
  return { from: patch.date_from ?? current.dates.from, to: patch.date_to ?? current.dates.to };
}

// Fields that don't apply to this line-item type — rejected loudly rather than silently dropped.
export function getInapplicableFields(
  lineItemType: LineItemType,
  patch: UpdateLineItemPatch
): string[] {
  const bad: string[] = [];
  if (patch.unit_price !== undefined && lineItemType !== 'standard') bad.push('unit_price');
  if (patch.media_budget !== undefined && lineItemType !== 'management_fee') {
    bad.push('media_budget');
  }
  if (patch.estimated_units !== undefined && lineItemType !== 'zero_dollar') {
    bad.push('estimated_units');
  }
  // Zero-dollar price is forced to 0 by the form/API; a price patch is nonsensical.
  if (patch.price !== undefined && lineItemType === 'zero_dollar') bad.push('price');
  return bad;
}

/**
 * Turn a patch's pacing consequence into concrete { pacing_type, pacing_details,
 * monthly_rollover }, or null when pacing is untouched. Precedence:
 *   1. explicit `patch.pacing` intent (lifetime / monthly / blocks)
 *   2. lifetime auto-sync: lifetime pacing tracks the flight and the paced total
 *      (mirrors the form reducer's SET_PRICE / SET_DATES behavior in lifetime mode)
 *   3. implicit proportional rescale when only the paced total moved (generalizes the old
 *      standard-only price rescale to all types via calcPatchedTotalPaced)
 *   4. null — notably: a dates-only change on CUSTOM pacing leaves blocks alone, so the
 *      validator's blocking block_outside_flight surfaces on the card.
 */
export function resolveLineItemPacing(
  current: PacingPatchCurrent,
  patch: UpdateLineItemPatch,
  promoted: boolean
): ResolvedPacing | null {
  const dates = mergePatchedDates(current, patch);
  const total = calcEffectiveTotalPaced(current, patch, promoted);

  const intent = patch.pacing;
  if (intent?.mode === 'lifetime') {
    // chk_lifetime_monthly_rollover: lifetime forces rollover on.
    return {
      pacing_type: 'lifetime',
      pacing_details: [{ dates, budget: total }],
      monthly_rollover: true
    };
  }
  if (intent?.mode === 'monthly') {
    // 'monthly' is a block-generation mode; the stored type is 'custom' ('monthly' is a
    // deprecated DB enum value).
    return {
      pacing_type: 'custom',
      pacing_details: generateMonthlyBlocks(dates, total),
      monthly_rollover: intent.monthly_rollover ?? current.monthly_rollover
    };
  }
  if (intent?.mode === 'blocks') {
    return {
      pacing_type: 'custom',
      pacing_details: intent.blocks.map(b => ({
        dates: { from: b.date_from, to: b.date_to },
        budget: b.budget
      })),
      monthly_rollover: intent.monthly_rollover ?? current.monthly_rollover
    };
  }

  const currentTotal = calcPatchedTotalPaced(current, {});
  const datesChanged = dates.from !== current.dates.from || dates.to !== current.dates.to;
  if (current.pacing_type === 'lifetime' && (datesChanged || total !== currentTotal)) {
    return {
      pacing_type: 'lifetime',
      pacing_details: [{ dates, budget: total }],
      monthly_rollover: true
    };
  }
  if (total !== currentTotal) {
    return {
      pacing_type: current.pacing_type,
      pacing_details: rescalePacingToTotal(current.pacing_details, currentTotal, total),
      monthly_rollover: current.monthly_rollover
    };
  }
  return null;
}
